import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DrawerProps } from 'antd';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CloseOutlined } from '@ant-design/icons';
import { DnDFiles, makeFiles } from '@dynamika-shared/ui/DnDFiles/DnDFiles';
import { UploadBoxIcon } from '@dynamika-shared/ui/icons';
import { twJoin } from '@utils/twMerge';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { FileError, FileValidationError } from './FileError';
import { useAttachFiles } from '@hooks/useAttachFiles';
import { LoadingFile } from '@components/SiteUserUpload/Components/Steps/components/LoadingFile';
import { convertDataSize } from '@utils/convertDataSize';
import {
  uploadExternalUserReportFailure,
  uploadExternalUserReportSuccess,
  useExternalUserReportsSlice,
} from '@restore/ExternalUserReports/ExternalUserReports.slice';
import { subscribeToActions } from '@restore/store';

type UploadReportDrawerProps = DrawerProps & {
  open: boolean;
  close: () => void;
  visitId: string;
  siteId: string;
};

const allowedFileExtensions = new Set(['zip', 'xlsx', 'pdf']);
const maxFileSizeInBytes = 50 * 1024 * 1024;
const maxFileCount = 20;

const validateFiles = (files: File[]) => {
  const validFiles: File[] = [];
  let error: FileValidationError | null = null;

  files.forEach(file => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (!allowedFileExtensions.has(extension)) {
      error = error ?? 'format';
      return;
    }

    if (file.size > maxFileSizeInBytes) {
      error = error ?? 'size';
      return;
    }

    validFiles.push(file);
  });

  return { validFiles, error };
};

export const UploadReportDrawer: FC<UploadReportDrawerProps> = ({
  open,
  close,
  visitId,
  siteId,
}) => {
  const { patientId, clinicalTrialId } = useParams<{
    patientId: string;
    clinicalTrialId?: string;
  }>();

  const [fileList, setFileList] = useState<File[]>([]);
  const [fileError, setFileError] = useState<FileValidationError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { onAttachFiles, uploadingFiles, removeFile, resetFiles } = useAttachFiles(setFileList);

  const [uploadExternalUserReport] = useExternalUserReportsSlice((_, a) => [
    a.uploadExternalUserReport,
  ]);

  const onCloseDrawer = () => {
    setFileList([]);
    setFileError(null);
    setIsSubmitting(false);
    resetFiles();
    close();
  };

  useEffect(() => {
    if (!isSubmitting) {
      return;
    }

    const unsubscribeSuccess = subscribeToActions(uploadExternalUserReportSuccess, () => {
      onCloseDrawer();
    });

    const unsubscribeFailure = subscribeToActions(uploadExternalUserReportFailure, () => {
      setIsSubmitting(false);
    });

    return () => {
      unsubscribeSuccess();
      unsubscribeFailure();
    };
  }, [isSubmitting]);

  const addFiles = useCallback(
    (files: File[]) => {
      const { validFiles, error } = validateFiles(files);

      if (error) {
        setFileError(error);
      }

      if (validFiles.length === 0) {
        return;
      }

      setFileList(prev => {
        const updatedFiles = [...prev, ...validFiles];

        if (updatedFiles.length > maxFileCount) {
          setFileError('limit');
          return prev;
        }

        setFileError(null);
        onAttachFiles(updatedFiles);
        return updatedFiles;
      });
    },
    [onAttachFiles]
  );

  const handleFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addFiles(makeFiles(e.target.files));
        e.target.value = '';
      }
    },
    [addFiles]
  );

  if (!clinicalTrialId || !patientId) return;

  const hasError = fileError !== null;

  return (
    <Drawer
      data-testid="upload-report-drawer"
      classNames={{
        header: '!px-0 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !pl-0 !pr-0',
        content: 'rounded-lg',
      }}
      title={'Upload Report'}
      open={open}
      width={464}
      placement="right"
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined data-testid="close-drawer-button" />}
    >
      {isSubmitting ? (
        <div className="flex h-full items-center justify-center">
          <LoadingFile
            text="Uploading files..."
            subTitle={'File upload can take some time.\nDo not close this modal.'}
          />
        </div>
      ) : (
      <>
      <div>
        <h3 className="text-1424 font-semibold">Upload</h3>
        <p className="mb-1 text-1216 text-dimGray">
          Only xlsx, .pdf, .zip formats are supported.
          <br />
          File must not exceed 50 MB
        </p>
        {fileError && <FileError type={fileError} />}
        <div className="mb-2">
          <DnDFiles onFilesAdded={addFiles}>
            {isDragActive => (
              <label
                className={twJoin(
                  'flex cursor-pointer flex-col items-center justify-center rounded-sm border border-pastelBlue py-4 transition duration-300 hover:bg-waterLight hover:bg-opacity-50',
                  isDragActive && 'border-vividTangelo opacity-30',
                  hasError && 'border-solid border-vermilion',
                  (!hasError || isDragActive) && 'border-dashed'
                )}
              >
                <div className="flex flex-col items-center">
                  <div className="mb-2 h-max rounded-full border-2 border-vividTangelo p-2 ">
                    <UploadBoxIcon className="h-4 w-4 text-vividTangelo" />
                  </div>
                  <div className="flex flex-col items-center font-medium">
                    <span className="text-1624 text-black">Upload or select</span>
                    <span className="text-vividTangelo">click to browse</span>
                    <input
                      className="hidden"
                      type="file"
                      multiple
                      accept=".xlsx,.pdf,.zip"
                      onChange={handleFile}
                    />
                  </div>
                </div>
              </label>
            )}
          </DnDFiles>
        </div>
        {uploadingFiles.length > 0 && (
          <>
            <h6 className="mb-1 text-1016 text-dimGray">Uploaded Files</h6>
            <ul className="max-h-[35vh] overflow-y-auto">
              {uploadingFiles.map(file => (
                <li
                  key={file.name}
                  className="mb-1 flex items-center justify-between rounded-md border border-brightGray bg-body py-[6px] pl-2 pr-1 text-1420"
                >
                  <TruncatedText
                    text={file.name}
                    className={'mr-0.5 max-w-[240px] text-dimGray'}
                    truncate
                    tooltip
                  />
                  <span className="ml-auto text-black">({convertDataSize(file.size)})</span>
                  <CloseOutlined
                    className="ml-1 cursor-pointer"
                    onClick={() => removeFile(file)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <footer className="mt-auto">
        <hr className="mb-3" />
        <div className="flex justify-between">
          <Button
            className="w-[17rem]"
            onClick={onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            data-testid="upload-report-upload-btn"
            className="w-[17rem]"
            type="primary"
            disabled={uploadingFiles.length === 0 || isSubmitting}
            onClick={() => {
              setIsSubmitting(true);
              uploadExternalUserReport({
                clinicalTrialId,
                siteId,
                patientId,
                visitId,
                files: fileList,
              });
            }}
          >
            Upload
          </Button>
        </div>
      </footer>
      </>
      )}
    </Drawer>
  );
};
